import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Pickaxe, Loader2, Home, ShieldCheck } from "lucide-react";
import { ComingSoonRibbon } from "../components/ComingSoonRibbon";
import { PopiaDialog, POPIA_TITLE } from "../components/Popia";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <ComingSoonRibbon />
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[#1E252B]">
        <img
          src="/signin-mine.jpg"
          alt="Assmang mining operation"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E252B] via-[#1E252B]/50 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-md bg-[#F2B50C] flex items-center justify-center">
              <Pickaxe className="h-6 w-6 text-[#0A2540]" />
            </div>
            <span className="font-display font-extrabold text-white text-xl tracking-tight">ASSMANG SMME PORTAL</span>
          </div>
          <div>
            <h2 className="font-display text-4xl font-black text-white leading-tight">
              Supplier & Procurement Portal
            </h2>
            <p className="text-slate-300 mt-4 max-w-md">
              Connecting Northern Cape SMMEs with opportunities at Black Rock, Khumani & Beeshoek mining operations.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-white relative">
        <Link to="/" data-testid="login-home-button"
          className="absolute top-5 right-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#00427A] border border-slate-200 hover:border-[#00427A] rounded-md px-3 py-1.5 transition-colors">
          <Home className="h-4 w-4" /> Home
        </Link>
        <form onSubmit={submit} className="w-full max-w-sm">
          <Link to="/" data-testid="login-logo" className="inline-block mb-6">
            <img src="/assmang-logo.png" alt="Assmang logo" className="h-16 w-auto" />
          </Link>
          <h1 className="font-display text-3xl font-black text-slate-900 tracking-tight">Sign In</h1>
          <p className="text-slate-500 mt-2 mb-8">Access your supplier portal account.</p>

          {error && (
            <div data-testid="login-error" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
          <input
            data-testid="login-email-input"
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none mb-4"
            placeholder="you@company.co.za"
          />
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
          <input
            data-testid="login-password-input"
            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none mb-6"
            placeholder="••••••••"
          />
          <button
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full bg-[#00427A] hover:bg-[#00325C] text-white font-semibold py-2.5 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
          </button>
          <p className="text-sm text-center mt-4">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-[#00427A] font-semibold hover:underline">
              Forgot password?
            </Link>
          </p>
          <p className="text-sm text-slate-500 mt-4 text-center">
            Don't have an account?{" "}
            <Link to="/register" data-testid="go-register-link" className="text-[#00427A] font-semibold hover:underline">
              Register
            </Link>
          </p>
          <div className="mt-8 pt-5 border-t border-slate-200 text-xs text-slate-500 leading-relaxed">
            <p className="flex items-center gap-1.5 font-semibold text-slate-600 mb-1">
              <ShieldCheck className="h-3.5 w-3.5 text-[#00427A]" /> {POPIA_TITLE}
            </p>
            <p>
              Assmang SMME Portal operates as a Responsible Party under the Protection of Personal
              Information Act, 4 of 2013 (POPIA). We process your identification, compliance and
              activity data to facilitate procurement and maintain a compliant audit trail.{" "}
              <PopiaDialog trigger={
                <button type="button" data-testid="login-popia-link" className="text-[#00427A] font-semibold hover:underline">
                  Read the full disclaimer
                </button>
              } />
            </p>
          </div>
        </form>
      </div>
    </div>
    </>
  );
}
